import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import ErpTemplate from '../../../components/ErpTemplate';
import { FaArrowLeft, FaExchangeAlt, FaTimes, FaTrash, FaFileInvoiceDollar, FaRegClock, FaCheckCircle, FaSync, FaDownload, FaUniversity, FaChevronDown, FaChevronRight, FaCalendarAlt, FaSearch } from 'react-icons/fa';
import api from '../../../services/api';
import ReactSelect from '../../../components/ReactSelect';
import { FinValor } from '../../../components/FinField';
import { OrigemMoeda } from '../../../components/ValorComOrigem';
import { paraNumero } from '../../../util/mascaraNumero';
import { toast } from 'react-toastify';
import { useSetFullBreadcrumbTrail } from '../../../context/BreadcrumbContext';
import { rotaDetalheAdiantamento } from './adiantamentoDetalhePolicy';
import '../../../css/financeiro-parcela-pagar.css';

// Tela "Adiantamentos · Saldo por evento" (razão evento_conta_movimento).
// Segue o shell padrão do financeiro (breadcrumb + .fin-pp__head + regiões com
// grupos accordion por cliente e tabela .fin-pp__tbl por evento — modelo:
// financeiroParcelaPagar). Duas regiões: Solicitados (a aprovar) e Já caiu (razão).
export default function PainelSaldoAdiantamento() {
    const history = useHistory();
    const token = localStorage.getItem('token');
    const setFullTrail = useSetFullBreadcrumbTrail();

    const [abaAtiva, setAbaAtiva] = useState('solicitados'); // 'solicitados' | 'saldo' | 'remanejamentos'
    const [saldosConta, setSaldosConta] = useState([]);
    const [carregandoConta, setCarregandoConta] = useState(false);
    const [contaExpandida, setContaExpandida] = useState(() => new Set());
    const [extratoAberto, setExtratoAberto] = useState(() => new Set()); // evento_id com extrato aberto
    const [solicitados, setSolicitados] = useState([]);
    const [solicExpandido, setSolicExpandido] = useState(() => new Set());
    const [transferModal, setTransferModal] = useState(null);
    const [salvandoTransfer, setSalvandoTransfer] = useState(false);
    const [extratos, setExtratos] = useState({});
    const [usarModal, setUsarModal] = useState(null);
    const [remanejamentos, setRemanejamentos] = useState(null);
    // Bolsão Axis (reunião 14/08): fundo interno que empresta verba entre eventos.
    // Fica nesta tela, e não em outra, porque é aqui que a Kátia já está quando
    // pensa em remanejar — o saque do Bolsão usa a mesma transferência de sempre.
    const [bolsao, setBolsao] = useState(null);
    const [aporteModal, setAporteModal] = useState(null);
    const [salvandoAporte, setSalvandoAporte] = useState(false);
    const [carregandoRemanej, setCarregandoRemanej] = useState(false);
    const [filtroRemanej, setFiltroRemanej] = useState('');

    const moeda = (value) => parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const LABEL_TIPO_MOV = {
        credito_adiantamento: '💰 Adiantamento creditado',
        debito_uso: '🧾 Uso',
        transferencia_saida: '➡️ Remanejado (saída)',
        transferencia_entrada: '⬅️ Recebido (entrada)',
        aporte: '🏦 Aporte da Axis',
        estorno: '↩️ Estorno',
        ajuste_inicial: '⚙️ Carga inicial',
    };

    const buscarBolsao = () => {
        api({ method: 'get', action: '/eventoConta/bolsao', token: token })
            .then(r => { setBolsao(r.data.existe ? r.data.data : null); })
            .catch(() => { setBolsao(null); });
    };

    const criarBolsao = () => {
        api({ method: 'post', action: '/eventoConta/bolsao/criar', token: token, data: {} })
            .then(() => { toast.success('Bolsão criado.'); buscarBolsao(); })
            .catch(e => { toast.error(e.response?.data?.mensagem || 'Erro ao criar o Bolsão.'); });
    };

    const salvarAporte = () => {
        const m = aporteModal;
        if (!m) return;
        const valor = paraNumero(m.valor) || 0; // valor vem do FinValor ("1500.5"); tirar os pontos dava 15005
        if (valor <= 0) { toast.error('Informe um valor maior que zero.'); return; }
        if (!String(m.observacao || '').trim()) { toast.error('Descreva a origem do dinheiro.'); return; }
        setSalvandoAporte(true);
        api({ method: 'post', action: '/eventoConta/bolsao/aportar', token: token,
              data: { valor: valor, observacao: m.observacao } })
            .then(() => {
                toast.success('Aporte registrado no Bolsão.');
                setAporteModal(null);
                buscarBolsao();
                buscarSaldosConta();
            })
            .catch(e => { toast.error(e.response?.data?.mensagem || 'Erro ao registrar o aporte.'); })
            .finally(() => setSalvandoAporte(false));
    };

    const buscarSaldosConta = () => {
        setCarregandoConta(true);
        buscarBolsao();
        api({ method: 'get', action: '/eventoConta/saldos', token: token, params: {} })
            .then(response => { setSaldosConta(response.data.data || []); })
            .catch(() => { })
            .finally(() => setCarregandoConta(false));
    };

    const buscarSolicitados = () => {
        api({ method: 'get', action: '/financeiroTitulo/adiantamentosSolicitados', token: token })
            .then(r => { setSolicitados(r.data.data || []); })
            .catch(() => { setSolicitados([]); });
    };

    const rodarBackfillConta = () => {
        setCarregandoConta(true);
        api({ method: 'post', action: '/eventoConta/backfill', token: token })
            .then(() => { buscarSaldosConta(); buscarSolicitados(); })
            .catch(() => { setCarregandoConta(false); });
    };

    const buscarExtratoEvento = (eventoId) => {
        api({ method: 'get', action: '/eventoConta/movimentos', token: token, params: { evento_id: eventoId } })
            .then(r => { setExtratos(prev => ({ ...prev, [eventoId]: r.data.data || [] })); })
            .catch(() => { });
    };

    const toggleExtrato = (eventoId) => {
        setExtratoAberto(prev => {
            const n = new Set(prev);
            if (n.has(eventoId)) { n.delete(eventoId); }
            else { n.add(eventoId); if (!Object.prototype.hasOwnProperty.call(extratos, eventoId)) buscarExtratoEvento(eventoId); }
            return n;
        });
    };

    const salvarTransferencia = () => {
        const t = transferModal;
        if (!t || !t.origem || !t.destino || !(parseFloat(t.valor) > 0)) { toast.warning('Preencha origem, destino e um valor maior que zero.'); return; }
        if (t.origem === t.destino) { toast.warning('Origem e destino devem ser eventos diferentes.'); return; }
        setSalvandoTransfer(true);
        api({ method: 'post', action: '/eventoConta/transferir', token: token, data: { evento_origem_id: t.origem, evento_destino_id: t.destino, valor: parseFloat(t.valor), observacao: t.obs || null } })
            .then(r => {
                if (r.data && r.data.origem_negativa) { toast.warning('Transferência feita — atenção: o evento de origem ficou com saldo negativo.'); }
                else { toast.success('Saldo remanejado!'); }
                setTransferModal(null);
                buscarSaldosConta();
                if (remanejamentos !== null) { buscarRemanejamentos(); }
            })
            .catch(err => { toast.error('Erro: ' + (err.response?.data?.mensagem ?? 'Erro na transferência')); })
            .finally(() => setSalvandoTransfer(false));
    };

    const salvarUso = () => {
        const u = usarModal;
        if (!u || !(parseFloat(u.valor) > 0)) { toast.warning('Informe um valor maior que zero.'); return; }
        setSalvandoTransfer(true);
        api({ method: 'post', action: '/eventoConta/usar', token: token, data: { evento_id: u.evento_id, valor: parseFloat(u.valor), observacao: u.obs || null } })
            .then(r => {
                if (r.data && r.data.saldo_negativo) { toast.warning('Uso registrado — atenção: o evento ficou com saldo negativo.'); }
                else { toast.success('Uso registrado!'); }
                setUsarModal(null);
                buscarSaldosConta();
                buscarExtratoEvento(u.evento_id);
            })
            .catch(err => { toast.error('Erro: ' + (err.response?.data?.mensagem ?? 'Erro ao registrar uso')); })
            .finally(() => setSalvandoTransfer(false));
    };

    const removerMovimento = (movId, eventoId) => {
        api({ method: 'post', action: '/eventoConta/remover/' + movId, token: token })
            .then(() => { toast.success('Movimento removido.'); buscarSaldosConta(); buscarExtratoEvento(eventoId); if (remanejamentos !== null) { buscarRemanejamentos(); } })
            .catch(err => { toast.error('Erro: ' + (err.response?.data?.mensagem ?? 'Erro ao remover')); });
    };

    const buscarRemanejamentos = () => {
        setCarregandoRemanej(true);
        api({ method: 'get', action: '/eventoConta/transferencias', token: token, params: {} })
            .then(r => { setRemanejamentos(r.data.data || []); })
            .catch(() => { setRemanejamentos([]); })
            .finally(() => setCarregandoRemanej(false));
    };

    const atualizarTudo = () => { buscarSaldosConta(); buscarSolicitados(); if (remanejamentos !== null) { buscarRemanejamentos(); } };

    const irParaAba = (aba) => {
        setAbaAtiva(aba);
        if (aba === 'remanejamentos' && remanejamentos === null) { buscarRemanejamentos(); }
    };

    useEffect(() => {
        setFullTrail([
            { path: '/financeiro', label: 'Financeiro' },
            { path: '/financeiroSaldoAdiantamento', label: 'Adiantamentos' },
        ]);
        buscarSaldosConta();
        buscarSolicitados();
        // eslint-disable-next-line
    }, []);

    // Deep-link de auditoria: ?evento=<id> (vindo do "ver extrato do adiantamento"
    // na parcela de Contas a Pagar) abre a aba Saldo já no extrato do evento.
    const location = useLocation();
    useEffect(() => {
        const eventoParam = new URLSearchParams(location.search).get('evento');
        if (!eventoParam || saldosConta.length === 0) return;
        const alvo = saldosConta.find(s => String(s.evento_id) === String(eventoParam));
        if (!alvo) return;
        setAbaAtiva('saldo');
        setContaExpandida(prev => { const n = new Set(prev); n.add(String(alvo.cliente_id || 'sem')); return n; });
        setExtratoAberto(prev => { const n = new Set(prev); n.add(alvo.evento_id); return n; });
        buscarExtratoEvento(alvo.evento_id);
        // eslint-disable-next-line
    }, [saldosConta, location.search]);

    const opcoesOrigemRemanejar = (saldosConta || [])
        .filter(s => parseFloat(s.saldo) > 0)
        .map(s => ({
            value: String(s.evento_id),
            label: `${s.cliente_nome ? s.cliente_nome + ' | ' : ''}${s.evento_codigo ? s.evento_codigo + ' ' : ''}${s.evento_nome || ''} · R$ ${moeda(parseFloat(s.saldo || 0))}`,
        }));

    // Saldo por evento (creditado) agrupado por cliente.
    const gruposSaldo = (() => {
        const porCliente = {};
        (saldosConta || []).forEach(s => {
            const k = s.cliente_id || 'sem';
            if (!porCliente[k]) porCliente[k] = { cliente_id: s.cliente_id, cliente_nome: s.cliente_nome, eventos: [], total: 0 };
            porCliente[k].eventos.push(s);
            porCliente[k].total += parseFloat(s.saldo || 0);
        });
        return Object.values(porCliente).sort((a, b) => b.total - a.total);
    })();
    // ─── Aba Remanejamentos: totais e filtro ──────────────────────────────
    const totalRemanejado = (remanejamentos || []).reduce((acc, r) => acc + parseFloat(r.valor || 0), 0);
    const eventosEnvolvidosRemanej = (function () {
        const set = new Set();
        (remanejamentos || []).forEach(r => {
            if (r.origem_evento_id) set.add(String(r.origem_evento_id));
            if (r.destino_evento_id) set.add(String(r.destino_evento_id));
        });
        return set.size;
    })();
    const remanejamentosFiltrados = (function () {
        const q = (filtroRemanej || '').trim().toLowerCase();
        if (!q) return (remanejamentos || []);
        return (remanejamentos || []).filter(r =>
            (r.origem_evento_nome || '').toLowerCase().includes(q)
            || (r.destino_evento_nome || '').toLowerCase().includes(q)
            || (r.origem_cliente || '').toLowerCase().includes(q)
            || (r.destino_cliente || '').toLowerCase().includes(q)
            || (r.usuario_nome || '').toLowerCase().includes(q)
            || (r.observacao || '').toLowerCase().includes(q)
        );
    })();

    const totalSaldo = gruposSaldo.reduce((acc, g) => acc + g.total, 0);
    const totalSolic = (solicitados || []).reduce((acc, g) => acc + parseFloat(g.total || 0), 0);

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">

                {/* Header */}
                <div className="fin-pp__head">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={() => history.push('/financeiro')} title="Voltar ao Financeiro">
                            <FaArrowLeft size={13} />
                        </button>
                        <div>
                            <h1 className="fin-pp__title">Adiantamentos · Saldo por evento</h1>
                            <p className="fin-pp__subtitle">Solicitados (a aprovar) e o que já caiu — o dinheiro é da Access, remanejável entre eventos.</p>
                        </div>
                    </div>
                    <div className="fin-pp__actions">
                        <button type="button" className="fin-pp__btn fin-pp__btn--primary" onClick={() => setTransferModal({ origem: null, destino: null, valor: '', obs: '' })}>
                            <FaExchangeAlt size={15} /> Remanejar
                        </button>
                        <button type="button" className="fin-pp__btn" onClick={atualizarTudo} disabled={carregandoConta}>
                            <FaSync size={14} /> {carregandoConta ? '...' : 'Atualizar'}
                        </button>
                        <button type="button" className="fin-pp__btn" onClick={rodarBackfillConta} disabled={carregandoConta}
                            title="Carga inicial: lança na conta os adiantamentos que já estão creditados">
                            <FaDownload size={14} /> Carregar creditados
                        </button>
                    </div>
                </div>

                {/* KPIs */}
                <section className="fin-pp__kpis" style={{ marginBottom: 18 }}>
                    <div className="fin-pp__kpi">
                        <div className="fin-pp__kpi-top">
                            <span className="fin-pp__kpi-label">Solicitado (a aprovar)</span>
                            <span className="fin-pp__kpi-ic"><FaRegClock size={16} /></span>
                        </div>
                        <div className="fin-pp__kpi-value is-warn">R$ {moeda(totalSolic)}</div>
                        <div className="fin-pp__kpi-sub">adiantamentos ainda não creditados</div>
                    </div>
                    <div className="fin-pp__kpi">
                        <div className="fin-pp__kpi-top">
                            <span className="fin-pp__kpi-label">Já caiu (creditado)</span>
                            <span className="fin-pp__kpi-ic"><FaCheckCircle size={16} /></span>
                        </div>
                        <div className="fin-pp__kpi-value is-ok">R$ {moeda(totalSaldo)}</div>
                        <div className="fin-pp__kpi-sub">saldo disponível para usar/remanejar</div>
                    </div>
                    <div className="fin-pp__kpi">
                        <div className="fin-pp__kpi-top">
                            <span className="fin-pp__kpi-label">Total Access</span>
                            <span className="fin-pp__kpi-ic"><FaUniversity size={16} /></span>
                        </div>
                        <div className="fin-pp__kpi-value">R$ {moeda(totalSolic + totalSaldo)}</div>
                        <div className="fin-pp__kpi-sub">solicitado + creditado</div>
                    </div>
                </section>

                {/* Abas */}
                <div className="fin-pp__tabs mb-3">
                    {[
                        { key: 'solicitados',    label: 'Solicitados · aguardando crédito' },
                        { key: 'saldo',          label: 'Saldo por Evento · já caiu' },
                        { key: 'remanejamentos', label: 'Remanejamentos' },
                    ].map(t => (
                        <button key={t.key} type="button" onClick={() => irParaAba(t.key)}
                            className={`fin-pp__tab${abaAtiva === t.key ? ' is-active' : ''}`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Aba: Remanejamentos (extrato consolidado) */}
                {abaAtiva === 'remanejamentos' && (
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaExchangeAlt size={16} /></span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <h2 className="fin-pp__cphead-title">Remanejamentos entre eventos</h2>
                                <p className="fin-pp__cphead-sub">
                                    Saldo que saiu de um evento e entrou em outro — de onde saiu → para onde foi. O dinheiro é da Access.
                                </p>
                            </div>
                            <span className="fin-pp__cphead-pill">{(remanejamentos || []).length} remanejamento{(remanejamentos || []).length !== 1 ? 's' : ''}</span>
                        </div>

                        <div className="fin-pp__region-body">
                            {/* KPIs — mesmo padrão das outras abas */}
                            <div className="fin-pp__kpis" style={{ padding: 14 }}>
                                <div className="fin-pp__kpi">
                                    <div className="fin-pp__kpi-top">
                                        <span className="fin-pp__kpi-label">Total remanejado</span>
                                        <span className="fin-pp__kpi-ic"><FaExchangeAlt size={16} /></span>
                                    </div>
                                    <div className="fin-pp__kpi-value">R$ {moeda(totalRemanejado)}</div>
                                    <div className="fin-pp__kpi-sub">soma de tudo que trocou de evento</div>
                                </div>
                                <div className="fin-pp__kpi">
                                    <div className="fin-pp__kpi-top"><span className="fin-pp__kpi-label">Movimentações</span></div>
                                    <div className="fin-pp__kpi-value">{(remanejamentos || []).length}</div>
                                    <div className="fin-pp__kpi-sub">pares origem → destino</div>
                                </div>
                                <div className="fin-pp__kpi">
                                    <div className="fin-pp__kpi-top"><span className="fin-pp__kpi-label">Eventos envolvidos</span></div>
                                    <div className="fin-pp__kpi-value">{eventosEnvolvidosRemanej}</div>
                                    <div className="fin-pp__kpi-sub">entre origem e destino</div>
                                </div>
                            </div>

                            {carregandoRemanej ? (
                                <div className="fin-pp__empty">Carregando…</div>
                            ) : (remanejamentos || []).length === 0 ? (
                                <div className="fin-pp__empty" style={{ padding: '28px 14px' }}>
                                    Nenhum remanejamento registrado ainda.
                                    <div style={{ fontSize: 12, color: 'var(--fin-mt)', marginTop: 6 }}>
                                        Use o botão <strong>Remanejar</strong> no topo para mover saldo de um evento para outro.
                                    </div>
                                </div>
                            ) : (
                                <div style={{ padding: '0 14px 14px' }}>
                                    {/* Filtro — mesmo padrão da aba Adiantamentos */}
                                    <div className="fin-pp__search" style={{ marginBottom: 12, boxShadow: 'none', border: 0, padding: 0 }}>
                                        <div className="fin-pp__search-field">
                                            <FaSearch className="fin-pp__search-ic" size={14} />
                                            <input className="fin-pp__input" placeholder="Filtrar por evento, cliente, responsável ou observação..."
                                                value={filtroRemanej} onChange={e => setFiltroRemanej(e.target.value)} />
                                        </div>
                                    </div>

                                    {remanejamentosFiltrados.length === 0 ? (
                                        <div className="fin-pp__empty">Nenhum remanejamento encontrado para "{filtroRemanej}".</div>
                                    ) : (
                                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                                            <div className="fin-pp__tblwrap">
                                                <table className="fin-pp__tbl fin-pp__tbl--wide" style={{ minWidth: 900 }}>
                                                    <thead><tr>
                                                        <th style={{ width: 100 }}>Data</th>
                                                        <th>Saiu de (origem)</th>
                                                        <th>Entrou em (destino)</th>
                                                        <th className="text-end" style={{ width: 140 }}>Valor</th>
                                                        <th style={{ width: 150 }}>Por</th>
                                                        <th style={{ width: 200 }}>Observação</th>
                                                    </tr></thead>
                                                    <tbody>
                                                        {remanejamentosFiltrados.map((r, i) => (
                                                            <tr key={r.par_id || i}>
                                                                <td className="fin-pp__cell-muted" style={{ whiteSpace: 'nowrap' }}>
                                                                    {r.data ? new Date(r.data + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}
                                                                </td>
                                                                <td>
                                                                    <div className="fin-pp__cell-strong">{r.origem_evento_nome || '—'}</div>
                                                                    {r.origem_cliente && (
                                                                        <div className="fin-pp__cell-muted">
                                                                            <FaUniversity size={10} style={{ marginRight: 4, opacity: 0.7 }} />{r.origem_cliente}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <div className="fin-pp__cell-strong">{r.destino_evento_nome || '—'}</div>
                                                                    {r.destino_cliente && (
                                                                        <div className="fin-pp__cell-muted">
                                                                            <FaUniversity size={10} style={{ marginRight: 4, opacity: 0.7 }} />{r.destino_cliente}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className="text-end fin-pp__cell-num" style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                                    R$ {moeda(r.valor)}
                                                                </td>
                                                                <td className="fin-pp__cell-muted">{r.usuario_nome || '—'}</td>
                                                                <td className="fin-pp__cell-muted">{r.observacao || '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div className="fin-pp__pagination">
                                                <div className="fin-pp__pagination-info">
                                                    {remanejamentosFiltrados.length} de {(remanejamentos || []).length} remanejamento{(remanejamentos || []).length !== 1 ? 's' : ''}
                                                </div>
                                                <div className="fin-pp__pagination-info">
                                                    Total exibido: <strong>R$ {moeda(remanejamentosFiltrados.reduce((s, r) => s + parseFloat(r.valor || 0), 0))}</strong>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* Aba: Solicitados (aguardando crédito) */}
                {abaAtiva === 'solicitados' && (
                <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                    <div className="fin-pp__cphead">
                        <span className="fin-pp__cphead-ic" style={{ background: 'var(--fin-wnb)', color: 'var(--fin-wn)' }}><FaRegClock size={16} /></span>
                        <div style={{ minWidth: 0 }}>
                            <h2 className="fin-pp__cphead-title">Solicitados · aguardando crédito</h2>
                            <p className="fin-pp__cphead-sub">Adiantamentos pedidos que ainda <strong>não caíram</strong>. Para creditar: Contas a Receber → aba Adiantamento.</p>
                        </div>
                        <span className="fin-pp__cphead-pill is-warn">R$ {moeda(totalSolic)}</span>
                    </div>
                    <div className="fin-pp__region-body">
                        {solicitados.length === 0 ? (
                            <div className="fin-pp__empty">Nenhum adiantamento aguardando crédito.</div>
                        ) : (
                            <div className="fin-pp__groups" style={{ padding: 14 }}>
                                {solicitados.map(g => {
                                    const key = String(g.cliente_id || 'sem');
                                    const aberto = solicExpandido.has(key);
                                    return (
                                        <div key={key} className="fin-pp__grp">
                                            <div className="fin-pp__grp-head"
                                                onClick={() => setSolicExpandido(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; })}>
                                                <span className="fin-pp__grp-caret">{aberto ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}</span>
                                                <span className="fin-pp__grp-ic"><FaUniversity size={15} /></span>
                                                <span className="fin-pp__grp-name">{g.cliente_nome}</span>
                                                <span className="fin-pp__grp-badge">{g.eventos.length} evento{g.eventos.length !== 1 ? 's' : ''}</span>
                                                <span className="fin-pp__grp-total">Total: <strong>R$ {moeda(g.total)}</strong></span>
                                            </div>
                                            {aberto && (
                                                <div className="fin-pp__tblwrap">
                                                    <table className="fin-pp__tbl">
                                                        <thead><tr>
                                                            <th>Evento</th>
                                                            <th style={{ width: 150 }}>Solicitado em</th>
                                                            <th>Por quem</th>
                                                            <th className="text-end" style={{ width: 90 }}>Itens</th>
                                                            <th className="text-end" style={{ width: 160 }}>Valor (a aprovar)</th>
                                                        </tr></thead>
                                                        <tbody>
                                                            {/* Uma linha POR ADIANTAMENTO, não por evento (Rafael
                                                                2026-08-10): antes o evento mostrava só o total somado
                                                                e não dava para ver quais solicitações o compunham.
                                                                O evento vira cabeçalho quando tem mais de um. */}
                                                            {g.eventos.map(function (ev, i) {
                                                                const lista = ev.adiantamentos || [];
                                                                if (!lista.length) {
                                                                    return (
                                                                        <tr key={(ev.evento_id || 'sem') + '_' + i}>
                                                                            <td className="fin-pp__cell-strong">{ev.evento_nome}<OrigemMoeda origem={ev.origem_moeda} comBrl /></td>
                                                                            <td className="fin-pp__cell-muted" colSpan={3}>—</td>
                                                                            <td className="text-end fin-pp__cell-num" style={{ color: 'var(--fin-wn)', fontWeight: 700 }}>R$ {moeda(ev.valor)}</td>
                                                                        </tr>
                                                                    );
                                                                }
                                                                return lista.map(function (a, j) {
                                                                    return (
                                                                        <tr key={(a.parcela_id || i) + '_' + j}
                                                                            {...(a.parcela_id ? { style: { cursor: 'pointer' }, title: 'Ver detalhes', onClick: function () { history.push(rotaDetalheAdiantamento(a.parcela_id)); } } : {})}>
                                                                            <td className="fin-pp__cell-strong">
                                                                                {j === 0 ? ev.evento_nome : ''}
                                                                                {j === 0 && ev.evento_codigo
                                                                                    ? <div className="fin-pp__cell-muted" style={{ fontSize: '0.8rem' }}>{ev.evento_codigo}</div>
                                                                                    : null}
                                                                                {j === 0 ? <OrigemMoeda origem={ev.origem_moeda} comBrl /> : null}
                                                                            </td>
                                                                            <td className="fin-pp__cell-muted">
                                                                                {a.solicitado_em ? a.solicitado_em.slice(8, 10) + '/' + a.solicitado_em.slice(5, 7) + '/' + a.solicitado_em.slice(0, 4) + ' ' + a.solicitado_em.slice(11, 16) : '—'}
                                                                            </td>
                                                                            <td className="fin-pp__cell-muted">{a.solicitado_por || '—'}</td>
                                                                            <td className="text-end fin-pp__cell-num">
                                                                                {a.itens > 0
                                                                                    ? <span className="fin-pp__minitag is-info">{a.itens}</span>
                                                                                    : <span className="fin-pp__cell-muted">—</span>}
                                                                            </td>
                                                                            <td className="text-end fin-pp__cell-num" style={{ color: 'var(--fin-wn)', fontWeight: 700 }}>R$ {moeda(a.valor)}</td>
                                                                        </tr>
                                                                    );
                                                                });
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </section>
                )}

                {/* Aba: Saldo por evento (já caiu) */}
                {abaAtiva === 'saldo' && (
                <section className="fin-pp__region">
                    <div className="fin-pp__cphead">
                        <span className="fin-pp__cphead-ic is-ok"><FaCheckCircle size={16} /></span>
                        <div style={{ minWidth: 0 }}>
                            <h2 className="fin-pp__cphead-title">Saldo por evento · já caiu</h2>
                            <p className="fin-pp__cphead-sub">Adiantamentos <strong>creditados</strong>, por evento (agrupado por cliente). É o que dá para usar/remanejar.</p>
                        </div>
                        <span className="fin-pp__cphead-pill" style={{ background: 'var(--fin-okb)', color: 'var(--fin-ok)' }}>Total Access: R$ {moeda(totalSaldo)}</span>
                    </div>
                    <div className="fin-pp__region-body">
                        {/* Bolsão Axis — o fundo aparece ANTES dos eventos porque é a
                            primeira pergunta da Kátia quando falta verba: "tem dinheiro
                            na Axis?". Emprestar dali é a transferência de sempre, com o
                            Bolsão na origem. */}
                        {!bolsao && (
                            <div className="fin-pp__card" style={{ marginBottom: 14, borderStyle: 'dashed' }}>
                                <div className="d-flex align-items-center gap-3 flex-wrap" style={{ padding: '14px 16px' }}>
                                    <span style={{ fontSize: '1.4rem', lineHeight: 1, opacity: 0.5 }}>🏦</span>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div className="fw-semibold" style={{ color: 'var(--fin-tx)' }}>Bolsão Axis ainda não criado</div>
                                        <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                                            Fundo interno para emprestar verba a um evento quando o adiantamento do cliente ainda não caiu.
                                        </div>
                                    </div>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={criarBolsao}>
                                        Criar Bolsão
                                    </button>
                                </div>
                            </div>
                        )}
                        {bolsao && (
                            <div className="fin-pp__card" style={{ marginBottom: 14, borderColor: 'var(--fin-acb)', background: 'var(--fin-acw)' }}>
                                <div className="d-flex align-items-center gap-3 flex-wrap" style={{ padding: '14px 16px' }}>
                                    <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>🏦</span>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div className="fw-bold" style={{ color: 'var(--fin-tx)', fontSize: 15 }}>{bolsao.nome}</div>
                                        <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                                            Fundo interno da Axis. Empresta verba a eventos quando o adiantamento do cliente ainda não caiu.
                                        </div>
                                    </div>
                                    <span className="fin-pp__pill is-info" style={{ fontSize: 14 }}>
                                        Disponível: R$ {moeda(bolsao.saldo)}
                                    </span>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                        onClick={() => setAporteModal({ valor: '', observacao: '' })}>
                                        + Registrar aporte
                                    </button>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--sm"
                                        disabled={!(parseFloat(bolsao.saldo) > 0)}
                                        title={parseFloat(bolsao.saldo) > 0 ? 'Emprestar para um evento' : 'Sem saldo no Bolsão'}
                                        onClick={() => setTransferModal({ origem: String(bolsao.evento_id), destino: '', valor: '', observacao: '' })}>
                                        <FaExchangeAlt size={12} /> Emprestar a um evento
                                    </button>
                                </div>
                            </div>
                        )}
                        {gruposSaldo.length === 0 ? (
                            <div className="fin-pp__empty">
                                Nenhum saldo ainda. Use <strong>Carregar creditados</strong> ou credite um adiantamento em Contas a Receber.
                            </div>
                        ) : (
                            <div className="fin-pp__groups" style={{ padding: 14 }}>
                                {gruposSaldo.map(g => {
                                    const key = String(g.cliente_id || 'sem');
                                    const aberto = contaExpandida.has(key);
                                    return (
                                        <div key={key} className="fin-pp__grp">
                                            <div className="fin-pp__grp-head"
                                                onClick={() => setContaExpandida(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; })}>
                                                <span className="fin-pp__grp-caret">{aberto ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}</span>
                                                <span className="fin-pp__grp-ic"><FaUniversity size={15} /></span>
                                                <span className="fin-pp__grp-name">{g.cliente_nome}</span>
                                                <span className="fin-pp__grp-badge">{g.eventos.length} evento{g.eventos.length !== 1 ? 's' : ''}</span>
                                                <span className="fin-pp__grp-total" style={{ color: g.total < 0 ? 'var(--fin-dg)' : 'var(--fin-ok)' }}>Total: <strong>R$ {moeda(g.total)}</strong></span>
                                            </div>
                                            {aberto && (
                                                <div className="fin-pp__tblwrap">
                                                    <table className="fin-pp__tbl fin-pp__tbl--wide">
                                                        <thead><tr>
                                                            <th>Evento</th>
                                                            <th className="text-end" style={{ width: 150 }}>Saldo</th>
                                                            <th style={{ width: 200 }}>Ações</th>
                                                        </tr></thead>
                                                        <tbody>
                                                            {g.eventos.map(ev => {
                                                                const eKey = ev.evento_id;
                                                                const extAberto = extratoAberto.has(eKey);
                                                                const movs = extratos[eKey] || [];
                                                                return (
                                                                    <React.Fragment key={eKey}>
                                                                        <tr>
                                                                            <td>
                                                                                <div className="fin-pp__cell-strong">{ev.evento_nome}</div>
                                                                                {ev.evento_codigo && <div className="fin-pp__cell-muted">{ev.evento_codigo}</div>}
                                                                                {/* Item 15: fornecedores do evento pagos em moeda estrangeira (o saldo é R$). */}
                                                                                <OrigemMoeda origem={ev.origem_moeda} comBrl />
                                                                            </td>
                                                                            <td className="text-end fin-pp__cell-num" style={{ fontWeight: 700, color: parseFloat(ev.saldo) < 0 ? 'var(--fin-dg)' : 'var(--fin-tx)' }}>R$ {moeda(parseFloat(ev.saldo || 0))}</td>
                                                                            <td>
                                                                                <div style={{ display: 'flex', gap: 8 }}>
                                                                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={() => setUsarModal({ evento_id: eKey, nome: ev.evento_nome, valor: '', obs: '' })}>Usar</button>
                                                                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={() => toggleExtrato(eKey)}>{extAberto ? 'Ocultar' : 'Extrato'}</button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                        {extAberto && (
                                                                            <tr className="fin-pp__tbl-sub">
                                                                                <td colSpan={3}>
                                                                                    {movs.length === 0 ? (
                                                                                        <div style={{ fontSize: 12, color: 'var(--fin-mt)', padding: '4px 2px' }}>Sem movimentos.</div>
                                                                                    ) : movs.map(m => (
                                                                                        <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5, padding: '3px 2px', borderBottom: '1px dashed var(--fin-bd)' }}>
                                                                                            <span style={{ color: 'var(--fin-mt)' }}>
                                                                                                {LABEL_TIPO_MOV[m.tipo] || m.tipo}{m.data ? ' · ' + new Date(m.data + 'T12:00:00').toLocaleDateString('pt-BR') : ''}
                                                                                                {m.observacao ? <span style={{ opacity: 0.8 }}> · {m.observacao}</span> : null}
                                                                                            </span>
                                                                                            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                                                <span style={{ color: m.sinal < 0 ? 'var(--fin-dg)' : 'var(--fin-ok)', fontWeight: 600 }}>{m.sinal < 0 ? '−' : '+'}R$ {moeda(parseFloat(m.valor || 0))}</span>
                                                                                                <span style={{ color: 'var(--fin-mt)' }}>= R$ {moeda(parseFloat(m.saldo || 0))}</span>
                                                                                                {/* 'aporte' entrou na lista (pendência do Bolsão): lançou errado, desfaz aqui.
                                                                                                    O servidor recusa se parte do aporte já foi emprestada — desfazer deixaria
                                                                                                    o fundo com saldo negativo sem lançamento explicando. */}
                                                                                                {['debito_uso', 'transferencia_saida', 'transferencia_entrada', 'aporte'].includes(m.tipo) && (
                                                                                                    <button type="button" title={m.tipo === 'aporte' ? 'Desfazer aporte' : 'Remover movimento'} style={{ border: 'none', background: 'none', color: 'var(--fin-dg)', cursor: 'pointer', padding: 0 }}
                                                                                                        onClick={() => { if (window.confirm(m.tipo === 'aporte' ? 'Desfazer este aporte? O valor sai do saldo do Bolsão.' : 'Remover este movimento?')) removerMovimento(m.id, eKey); }}><FaTrash size={11} /></button>
                                                                                                )}
                                                                                            </span>
                                                                                        </div>
                                                                                    ))}
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 6px 2px', fontWeight: 700, borderTop: '2px solid var(--fin-bd)', marginTop: 6 }}>
                                    <span>Total (Access)</span>
                                    <span style={{ color: totalSaldo < 0 ? 'var(--fin-dg)' : 'var(--fin-ok)' }}>R$ {moeda(totalSaldo)}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
                )}
            </div>

            {/* ── Modal: Remanejar saldo entre eventos (transferência A→B) ── */}
            {transferModal && (
                <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                    onClick={(e) => { if (e.target === e.currentTarget) setTransferModal(null); }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content fin-pp__modal">
                            <div className="fin-pp__modal-head">
                                <span className="fin-pp__modal-head-ic"><FaExchangeAlt size={16} /></span>
                                <h3 className="fin-pp__modal-title">Remanejar saldo
                                    <span className="fin-pp__modal-sub">Mover saldo de um evento para outro (fica registrado a origem → destino)</span>
                                </h3>
                                <button type="button" className="fin-pp__modal-close" onClick={() => setTransferModal(null)}><FaTimes size={16} /></button>
                            </div>
                            <div className="modal-body">
                                <div className="fin-pp__filters">
                                    <div className="fin-pp__field" style={{ flex: '1 1 100%' }}>
                                        <label className="fin-pp__label">Evento de ORIGEM (de onde sai)</label>
                                        <ReactSelect placeholder={opcoesOrigemRemanejar.length ? 'Selecione o evento de origem...' : 'Nenhum evento com saldo para remanejar'}
                                            isDisabled={opcoesOrigemRemanejar.length === 0}
                                            value={transferModal.origem ? { value: transferModal.origem, label: transferModal.nomeOrigem } : null}
                                            onChange={e => setTransferModal({ ...transferModal, origem: e?.value || null, nomeOrigem: e?.label || '' })}
                                            options={opcoesOrigemRemanejar} />
                                        <div style={{ fontSize: 11, color: 'var(--fin-mt)', marginTop: 3 }}>Só aparecem eventos que têm saldo (adiantamento creditado ou remanejamento recebido).</div>
                                    </div>
                                    <div className="fin-pp__field" style={{ flex: '1 1 100%' }}>
                                        <label className="fin-pp__label">Evento de DESTINO (para onde vai)</label>
                                        <ReactSelect placeholder="Buscar evento de destino..."
                                            value={transferModal.destino ? { value: transferModal.destino, label: transferModal.nomeDestino } : null}
                                            onChange={e => setTransferModal({ ...transferModal, destino: e?.value || null, nomeDestino: e?.label || '' })}
                                            loadOptions={(input) => api({ method: 'get', action: '/evento', token: token, params: { nome: input, situacaoRegistro: 'ativo' } })
                                                .then(r => (r.data.data || []).map(reg => ({ value: reg.id, label: `${reg.cliente && reg.cliente.nome ? reg.cliente.nome + ' | ' : ''}${reg.codigo ? reg.codigo + ' ' : ''}${reg.nome || ''}` })))} />
                                    </div>
                                    <div className="fin-pp__field">
                                        <label className="fin-pp__label">Valor</label>
                                        <FinValor allowNegative={false} moeda="real" decimalScale={2} fixedDecimalScale={false} placeholder="R$ 0,00"
                                            value={transferModal.valor} onValueChange={({ value }) => setTransferModal({ ...transferModal, valor: value })} />
                                    </div>
                                    <div className="fin-pp__field" style={{ flex: '1 1 100%' }}>
                                        <label className="fin-pp__label">Observação (opcional)</label>
                                        <input className="fin-pp__input" style={{ paddingLeft: 14 }} placeholder="motivo do remanejamento..."
                                            value={transferModal.obs} onChange={e => setTransferModal({ ...transferModal, obs: e.target.value })} />
                                    </div>
                                </div>
                                <div style={{ fontSize: 11.5, color: 'var(--fin-mt)', marginTop: 4 }}>O saldo é da Access — a origem pode ficar negativa (só avisa).</div>
                            </div>
                            <div className="fin-pp__modal-foot">
                                <button type="button" className="fin-pp__btn" onClick={() => setTransferModal(null)}>Cancelar</button>
                                <button type="button" className="fin-pp__btn fin-pp__btn--primary" onClick={salvarTransferencia}
                                    disabled={salvandoTransfer || !transferModal.origem || !transferModal.destino || !(parseFloat(transferModal.valor) > 0)}>
                                    {salvandoTransfer ? 'Remanejando...' : 'Remanejar'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Modal: Registrar uso do saldo de um evento ── */}
            {usarModal && (
                <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                    onClick={(e) => { if (e.target === e.currentTarget) setUsarModal(null); }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content fin-pp__modal">
                            <div className="fin-pp__modal-head">
                                <span className="fin-pp__modal-head-ic"><FaFileInvoiceDollar size={16} /></span>
                                <h3 className="fin-pp__modal-title">Registrar uso do saldo
                                    <span className="fin-pp__modal-sub">{usarModal.nome}</span>
                                </h3>
                                <button type="button" className="fin-pp__modal-close" onClick={() => setUsarModal(null)}><FaTimes size={16} /></button>
                            </div>
                            <div className="modal-body">
                                <div className="fin-pp__filters">
                                    <div className="fin-pp__field">
                                        <label className="fin-pp__label">Valor usado</label>
                                        <FinValor allowNegative={false} moeda="real" decimalScale={2} fixedDecimalScale={false} placeholder="R$ 0,00"
                                            value={usarModal.valor} onValueChange={({ value }) => setUsarModal({ ...usarModal, valor: value })} />
                                    </div>
                                    <div className="fin-pp__field" style={{ flex: '1 1 100%' }}>
                                        <label className="fin-pp__label">Observação (opcional)</label>
                                        <input className="fin-pp__input" style={{ paddingLeft: 14 }} placeholder="o que foi pago..."
                                            value={usarModal.obs} onChange={e => setUsarModal({ ...usarModal, obs: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                            <div className="fin-pp__modal-foot">
                                <button type="button" className="fin-pp__btn" onClick={() => setUsarModal(null)}>Cancelar</button>
                                <button type="button" className="fin-pp__btn fin-pp__btn--danger" onClick={salvarUso}
                                    disabled={salvandoTransfer || !(parseFloat(usarModal.valor) > 0)}>
                                    {salvandoTransfer ? 'Salvando...' : 'Registrar uso'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {aporteModal && (
                <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                     onClick={(e) => { if (e.target === e.currentTarget) setAporteModal(null); }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content fin-pp__modal">
                            <div className="fin-pp__modal-head">
                                <span className="fin-pp__modal-head-ic"><FaUniversity size={17} /></span>
                                <h3 className="fin-pp__modal-title">Registrar aporte no Bolsão
                                    <span className="fin-pp__modal-sub">Dinheiro da Axis entrando no fundo</span>
                                </h3>
                                <button type="button" className="fin-pp__modal-close" onClick={() => setAporteModal(null)}><FaTimes size={16} /></button>
                            </div>
                            <div className="modal-body">
                                <div className="fin-pp__filters">
                                    <div className="fin-pp__field">
                                        <label className="fin-pp__label">Valor *</label>
                                        <FinValor allowNegative={false} moeda="real" decimalScale={2} fixedDecimalScale={false} placeholder="R$ 0,00" value={aporteModal.valor}
                                            onValueChange={({ value }) => setAporteModal(Object.assign({}, aporteModal, { valor: value }))} />
                                    </div>
                                    <div className="fin-pp__field fin-pp__field--full">
                                        <label className="fin-pp__label">De onde veio esse dinheiro? *</label>
                                        <textarea className="fin-pp__input" style={{ height: 'auto', padding: '10px 14px' }} rows={2}
                                            placeholder="Ex.: risco sacado da NF 123 · sobra do evento Sanofi Jardins"
                                            value={aporteModal.observacao}
                                            onChange={(e) => setAporteModal(Object.assign({}, aporteModal, { observacao: e.target.value }))} />
                                        <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
                                            Obrigatório: sem a origem, o extrato do fundo vira uma coluna de números sem história.
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="fin-pp__modal-foot">
                                <button type="button" className="fin-pp__btn" onClick={() => setAporteModal(null)}>Cancelar</button>
                                <button type="button" className="fin-pp__btn fin-pp__btn--primary"
                                    disabled={salvandoAporte} onClick={salvarAporte}>
                                    {salvandoAporte ? 'Salvando...' : 'Registrar aporte'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </ErpTemplate>
    );
}
